import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, getToken } from "../../../software/api";
import { toast } from "sonner";
import { ImagePlus, Search, Check, X, Loader2, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PartTile({ p, onSet }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("That's not an image file."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/uploads`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Upload failed");
      const url = `${process.env.REACT_APP_BACKEND_URL}${d.url}`;
      await api("PATCH", `/products/${p.id}/image`, { image_url: url });
      onSet(p.id, url);
      toast.success(`Photo added to ${p.name}`);
    } catch (err) { toast.error(err.message || "Upload failed"); }
    finally { setBusy(false); }
  };

  const clear = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try { await api("PATCH", `/products/${p.id}/image`, { image_url: "" }); onSet(p.id, ""); }
    finally { setBusy(false); }
  };

  return (
    <div
      data-testid={`part-tile-${p.id}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
      className={`relative cursor-pointer rounded-2xl border-2 bg-white overflow-hidden transition-all ${drag ? "border-sky-500 ring-4 ring-sky-100" : p.image_url ? "border-emerald-200" : "border-dashed border-slate-300 hover:border-sky-400"}`}
    >
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={`part-file-${p.id}`} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      <div className="aspect-square bg-slate-50 flex items-center justify-center relative">
        {p.image_url ? (
          <>
            <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
            <span className="absolute top-1.5 left-1.5 h-6 w-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow"><Check className="h-3.5 w-3.5" /></span>
            <button onClick={clear} data-testid={`part-clear-${p.id}`} title="Remove photo" className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-white/90 text-rose-600 flex items-center justify-center shadow hover:bg-white"><X className="h-3.5 w-3.5" /></button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1 text-slate-400 px-2 text-center">
            {busy ? <Loader2 className="h-7 w-7 animate-spin text-sky-500" /> : <ImagePlus className="h-7 w-7" />}
            <span className="text-[11px] font-semibold">{busy ? "Uploading…" : "Tap or drop a photo"}</span>
          </div>
        )}
        {busy && p.image_url && <div className="absolute inset-0 bg-white/60 flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-sky-500" /></div>}
      </div>
      <div className="p-2.5">
        <p className="font-semibold text-sm text-slate-800 truncate" title={p.name}>{p.name}</p>
        <p className="text-[11px] text-slate-400 truncate">{p.sku ? `SKU ${p.sku}` : ""}{p.brand ? ` · ${p.brand}` : ""}</p>
      </div>
    </div>
  );
}

export default function PartPhotos() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [stats, setStats] = useState({ total: 0, withPhoto: 0 });

  const load = useCallback(async (search) => {
    setLoading(true);
    try {
      const all = await api("GET", `/products${search ? `?q=${encodeURIComponent(search)}` : ""}`);
      setItems(all);
      setStats({ total: all.length, withPhoto: all.filter((p) => p.image_url).length });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q, load]);

  const onSet = (id, url) => {
    setItems((xs) => xs.map((p) => p.id === id ? { ...p, image_url: url } : p));
    setStats((s) => ({ ...s, withPhoto: (items.find((p) => p.id === id)?.image_url ? s.withPhoto : s.withPhoto + (url ? 1 : 0)) - (!url && items.find((p) => p.id === id)?.image_url ? 1 : 0) }));
  };

  const shown = onlyMissing ? items.filter((p) => !p.image_url) : items;

  return (
    <div data-testid="part-photos-panel" className="space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 mb-1 flex items-center gap-2"><Camera className="h-6 w-6 text-sky-600" /> Part Photos</h1>
        <p className="text-sm text-slate-500">Snap or drag a photo straight onto any part. Photos show on the POS tiles, showroom and quotes.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="part-photos-search" placeholder="Search parts by name, SKU or barcode…" className="w-full rounded-xl border border-slate-300 pl-9 pr-3 py-2.5 text-sm focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none" />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="part-photos-missing-toggle">
          <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
          Only show parts without a photo
        </label>
        <span className="ml-auto text-sm text-slate-500" data-testid="part-photos-stats"><span className="font-bold text-emerald-600">{stats.withPhoto}</span> of {stats.total} have photos</span>
      </div>

      {loading ? <p className="text-slate-500 py-10 text-center">Loading parts…</p> : shown.length === 0 ? (
        <p className="text-slate-400 py-10 text-center">{onlyMissing ? "Every part in this view has a photo 🎉" : "No parts match your search."}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {shown.slice(0, 120).map((p) => <PartTile key={p.id} p={p} onSet={onSet} />)}
        </div>
      )}
      {shown.length > 120 && <p className="text-center text-xs text-slate-400">Showing first 120 — search to narrow down.</p>}
    </div>
  );
}
